// Elements arrive and leave with the scroll.
//
// The heavy lifting is CSS: `animation-timeline: view()` ties opacity to the
// element's position in the viewport, off the main thread. This file only
// marks the targets and turns the effect on.
//
// Two rules it follows, both learned the hard way:
//
// 1. Nothing is hidden until JavaScript is running. The hiding lives behind
//    `html.reveal-ready`, which this file adds. If the script fails to parse,
//    fails to load, or is blocked, every element renders at full opacity and
//    the page is merely un-animated rather than blank. A reveal effect that can
//    white out a storefront is not worth having.
//
// 2. Reduced motion is honoured by not running at all, rather than by shipping
//    a second animation.
//
// The IntersectionObserver below is ONLY a fallback for browsers without
// scroll-driven animations. Where they are supported it never runs, because a
// binary observer cannot express a fade-out you can actually see: "no longer
// intersecting" is true only once the element is already off screen.

// Every content block in every section, rather than a hand-picked list.
//
// The curated list was the mistake: it caught the reviews and the quote and
// missed the hero, the materials fold, the product card and the about block, so
// the page animated in patches. `.shopify-section > .section > *` is each
// section's own content children — headings, grids, media, groups — which is
// uniform, needs no maintenance when a section is added, and covers images and
// cards as readily as text.
//
// Two exclusions, both deliberate:
//   - `.section-background` / `.custom-section-background` are zero-height
//     colour layers. Animating them does nothing and costs a compositor layer.
//   - the product page's information grid holds the sticky buy panel, and a
//     transformed ancestor is a bad neighbour to sticky positioning. The
//     specification fold below it still reveals.
const REVEAL = ['.shopify-section > .section > *']

const SKIP = [
  '.section-background',
  '.custom-section-background',
  '.product-information__grid',
]

const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
const scrollDriven =
  typeof CSS !== 'undefined' &&
  CSS.supports &&
  CSS.supports('animation-timeline', 'view()')

if (!reduced) {
  const targets = document.querySelectorAll(REVEAL.join(','))

  if (targets.length) {
    // Anything already on screen when the page loads is simply there.
    //
    // The reveal is a function of scroll position, so an element sitting
    // partway up the viewport at scroll zero is partway through its entry
    // range — which rendered the first video dimmed on arrival, waiting for a
    // scroll that had not happened yet. A reader should never have to move the
    // page to see what is already in front of them.
    //
    // Measured BEFORE `reveal-ready` is added, so nothing is hidden yet and
    // every position is the natural one. This also handles a browser restoring
    // a scroll position on refresh: whatever the reader lands on is solid.
    const fold = window.innerHeight

    targets.forEach((el) => {
      if (el.matches(SKIP.join(','))) return
      const box = el.getBoundingClientRect()
      if (!box.height) return
      if (box.top < fold) return
      el.setAttribute('data-reveal', '')
    })
    document.documentElement.classList.add('reveal-ready')

    if (!scrollDriven && 'IntersectionObserver' in window) {
      document.querySelectorAll('[data-reveal]').forEach((el) => {
        // Siblings in a row arrive one after another rather than all at once.
        const siblings = el.parentElement ? [...el.parentElement.children] : [el]
        const index = siblings.indexOf(el)
        if (index > 0 && siblings.length > 1) {
          el.style.setProperty('--reveal-delay', `${Math.min(index, 4) * 110}ms`)
        }
      })

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) entry.target.classList.add('is-revealed')
          })
        },
        // Well inside the viewport, so nothing arrives at the very edge of the
        // screen. The first version triggered at 8% and read as too soon.
        { rootMargin: '0px 0px -24% 0px', threshold: 0 }
      )

      document.querySelectorAll('[data-reveal]').forEach((el) => observer.observe(el))
    }
  }
}
